import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { IoMdClose } from 'react-icons/io';
import aiLogo from 'Media/Amnius_connect_with_text.png';
import { fetchAllMessages, sendMessage } from 'Config/API/get';
import './AssetChatbot.css';
import { getFileIcon } from 'Constants/Functions';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import {
  assetuniqueid,
  setmessagehistory,
  ticketIdAction,
} from 'Redux/action/variable.action';
import { useNavigate } from 'react-router-dom';

export default function AssetChatbot({ loadingFile, onClose, visible = true }) {
  const token = localStorage.getItem('token');
  const file_id = useSelector((s) => s.aifileidreducer);
  const currentselectedfile = useSelector((s) => s.currentselectedfile);
  const reduxMessages = useSelector((s) => s.messagesReducer);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [assistantChanged, setAssistantChanged] = useState(false);
  const messagesEndRef = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const checkAssistantChanged = (res) =>
    res?.status === 200 && res?.data?.status === false;

  // ---- Load messages safely ----
  const getAllMessages = async (force = false) => {
    if (!file_id || sending) return; // ⛔ don't reload during send

    try {
      const res = await fetchAllMessages(token, file_id);
      if (checkAssistantChanged(res)) {
        setAssistantChanged(true);
        setMessages([]);
        return;
      }

      const fetched =
        res?.data?.status && Array.isArray(res.data?.data)
          ? res.data.data
          : [];

      // keep Redux cache
      const reduxForFile = reduxMessages.filter((m) => m.file_id === file_id);
      const merged = fetched.length ? fetched : reduxForFile;

      setMessages(merged);
      dispatch(setmessagehistory(merged));
    } catch (err) {
      console.error('Fetch messages error:', err);
    }
  };

  // 🔁 when file changes, load
  useEffect(() => {
    if (file_id) getAllMessages();
  }, [file_id]);

  // 🔁 when popup reopens, refresh only if idle
  useEffect(() => {
    if (visible && file_id && !sending) getAllMessages();
  }, [visible]);

  // scroll down
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ---- SEND MESSAGE ----
const handleSend = async () => {
  if (!input.trim() || !file_id) return;

  const text = input.trim();
  setInput('');

  const now = Date.now();

  // Add user message and thinking bubble
  const userMsg = { sender: 'user', text, timestamp: now, file_id };
  const thinkingMsg = {
    sender: 'assistant',
    text: 'Thinking...',
    thinking: true,
    timestamp: now + 1,
    file_id,
  };

  setMessages((prev) => {
    const updated = [...prev, userMsg, thinkingMsg];
    dispatch(setmessagehistory(updated));
    return updated;
  });

  setSending(true);

  try {
    const res = await sendMessage(token, file_id, text);
    if (checkAssistantChanged(res)) {
      setAssistantChanged(true);
      setMessages([]);
      return;
    }

    const replyText =
      res?.data?.data?.text ||
      res?.data?.data ||
      'No response received.';

    const reply = {
      sender: 'assistant',
      text: replyText,
      thinking: false,
      timestamp: Date.now(),
      file_id,
    };

    // ✅ Replace only the last thinking message — guaranteed clean
    setMessages((prev) => {
      const lastIndex = [...prev].reverse().findIndex((m) => m.thinking);
      let updated;

      if (lastIndex !== -1) {
        const realIndex = prev.length - 1 - lastIndex;
        updated = [
          ...prev.slice(0, realIndex),
          reply,
          ...prev.slice(realIndex + 1),
        ];
      } else {
        updated = [...prev, reply];
      }

      dispatch(setmessagehistory(updated));
      return updated;
    });
  } catch (error) {
    console.error('Send failed:', error);
    toast.error('Error sending message');

    // Replace the "Thinking..." message with an error message
    setMessages((prev) => {
      const lastIndex = [...prev].reverse().findIndex((m) => m.thinking);
      let updated;
      if (lastIndex !== -1) {
        const realIndex = prev.length - 1 - lastIndex;
        updated = [
          ...prev.slice(0, realIndex),
          { ...prev[realIndex], text: 'Error: failed to get reply.', thinking: false },
          ...prev.slice(realIndex + 1),
        ];
      } else {
        updated = prev;
      }

      dispatch(setmessagehistory(updated));
      return updated;
    });
  } finally {
    setSending(false);
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 50); // ensure scroll runs after DOM update
  }
};


  const handleNavigate = () => {
    if (currentselectedfile?.myAsset)
      dispatch(assetuniqueid(currentselectedfile?.myAsset?.unique_id));
    else
      dispatch(ticketIdAction(currentselectedfile?.editedAsset?.unique_id));

    navigate(
      currentselectedfile?.myAsset ? '/asset-details' : '/part-detail'
    );
    onClose();
  };

  const handleOpenFile = () => {
    localStorage.setItem('filePath', currentselectedfile?.path);
    localStorage.setItem(
      'fileType',
      currentselectedfile?.name?.split('.').pop()?.toLowerCase()
    );
    window.open(`${window.location.origin}/file-explorer/file-viewer`, '_blank');
  };

  return (
    <div className={`chatbot-popup ${visible ? 'open' : 'closed'}`}>
      <p
        style={{
          margin: 0,
          fontSize: '18px',
          color: '#446fa2',
          fontWeight: '600',
          textDecoration: 'underline',
          justifyContent: 'center',
          display: 'flex',
          backgroundColor: '#f5f7fa',
          paddingTop: '10px',
          cursor: 'pointer',
        }}
        onClick={handleNavigate}
      >
        {currentselectedfile?.myAsset?.assetsName ||
          currentselectedfile?.editedAsset?.partHash ||
          'AI Assistant'}
      </p>

      <div className="chatbot-header">
        <img
          src={getFileIcon(currentselectedfile) || aiLogo}
          alt="AI"
        />
        <div style={{ width: '300px', wordWrap: 'break-word' }}>
          <h2 style={{ color: '#446fa2', cursor: 'pointer' }} onClick={handleOpenFile}>
            {currentselectedfile?.name || 'AI Assistant'}
          </h2>
        </div>
        <span className="chatbot-close-button" onClick={onClose}>
          <IoMdClose size={20} />
        </span>
      </div>

      {loadingFile && (
        <div className="upload-progress-bar">
          <div className="progress-animation" />
          <p>Uploading file, please wait...</p>
        </div>
      )}

      {assistantChanged ? (
        <div className="chat-error-block">
          <div className="chat-error-title">⚠ Active Assistant Changed</div>
          <p className="chat-error-message">
            The active assistant for this file has changed. Please re-upload
            your file to continue chatting.
          </p>
        </div>
      ) : (
        <>
          <div className="chatbot-messages">
            {messages.map((msg, i) => (
              <div
                key={i}
                className={`message-bubble ${
                  msg.sender === 'user' ? 'user' : 'assistant'
                }`}
              >
                <ReactMarkdown
                  children={msg.text}
                  components={{
                    code({ inline, className, children, ...props }) {
                      const match = /language-(\w+)/.exec(className || '');
                      return !inline && match ? (
                        <SyntaxHighlighter
                          style={oneDark}
                          language={match[1]}
                          PreTag="div"
                          {...props}
                        >
                          {String(children).replace(/\n$/, '')}
                        </SyntaxHighlighter>
                      ) : (
                        <code className={className} {...props}>
                          {children}
                        </code>
                      );
                    },
                  }}
                />
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          <div className="chatbot-input">
            <input
              type="text"
              placeholder="Ask me anything..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSend()}
              disabled={loadingFile}
            />
            <button
              onClick={handleSend}
              disabled={sending || !input.trim() || loadingFile}
            >
              {sending ? 'Sending...' : 'Send'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
